import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FileText, Plus, Pencil, Trash2, X, Loader2, Search, Tag, Check } from 'lucide-react';
import { api } from '../../api/client';

/* ── Textos predeterminados de la ventana de Mensajes ─────────────────────────
 * Lista de textos guardados con etiquetas {{nombre}}, {{tracking_ml}}… Al elegir uno, las
 * etiquetas se reemplazan con los datos de ESTA venta y el resultado se pega en el cuadro de
 * respuesta para revisarlo/editarlo. Nada se envía desde acá.
 * ─────────────────────────────────────────────────────────────────────────── */

interface Template {
  id: number;
  /** null = compartido por todos los vendedores. */
  seller_id: number | null;
  name: string;
  body: string;
}

interface TagDef {
  key: string;
  label: string;
}

/** Tope de ML para un mensaje posventa (el mismo que usa el asistente). */
const ML_MAX_CHARS = 350;
const TAG_RE = /\{\{\s*([a-z_]+)\s*\}\}/g;

/** Reemplaza las etiquetas; las que no tienen dato quedan marcadas para completarlas a mano. */
export function fillTemplate(
  body: string,
  vars: Record<string, string>,
  tags: TagDef[]
): { text: string; missing: string[] } {
  const missing = new Set<string>();
  const text = body.replace(TAG_RE, (whole, key: string) => {
    if (!(key in vars)) return whole; // etiqueta desconocida: se deja tal cual
    const v = vars[key];
    if (v) return v;
    const label = tags.find((t) => t.key === key)?.label || key;
    missing.add(label);
    return `[COMPLETAR: ${label}]`;
  });
  return { text, missing: [...missing] };
}

interface Props {
  /** Camino de la conversación ya resuelto (`/mercadolibre/messages/conversations/123`). */
  resource: string;
  sellerId: number;
  /** Pega el texto ya completado en el cuadro de respuesta. */
  onInsert: (text: string, missing: string[]) => void;
  /**
   * `false`: solo elegir y pegar (sin Nuevo / Editar / Eliminar). Lo usa el pop-up del depósito
   * cuando el operario no tiene las llaves de Mensajes u Órdenes, que son las que pide el servidor
   * para crear, editar o borrar textos.
   */
  canEdit?: boolean;
  /** Lista más baja: el chat metido en el pop-up del depósito mide 460 px y la recortaba. */
  compact?: boolean;
}

export function MessageTemplates({ resource, sellerId, onInsert, canEdit = true, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [tags, setTags] = useState<TagDef[]>([]);
  const [vars, setVars] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // null = lista; 'new' = creando; número = editando ese id
  const [editing, setEditing] = useState<'new' | number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  // Solo quien ve todos los vendedores crea/edita/borra textos compartidos (lo decide el servidor).
  const [canManageShared, setCanManageShared] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, vRes] = (await Promise.all([
        api.get(`/mercadolibre/messages/templates?seller_id=${sellerId}`),
        api.get(`${resource}/template-vars?seller_id=${sellerId}`),
      ])) as any[];
      if (!tRes.success) throw new Error(tRes.error || 'No pude cargar los textos');
      setTemplates(tRes.data || []);
      setTags(tRes.meta?.tags || []);
      setCanManageShared(!!tRes.meta?.can_manage_shared);
      // Sin datos de la venta igual se pueden ver/editar los textos; al pegarlos, las etiquetas
      // quedan marcadas para completar.
      setVars(vRes?.success ? vRes.data : {});
    } catch (e: any) {
      setError(e.message || 'Error de red');
    } finally {
      setLoading(false);
    }
  }, [resource, sellerId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const applyTemplate = (t: Template) => {
    const { text, missing } = fillTemplate(t.body, vars || {}, tags);
    onInsert(text, missing);
    setOpen(false);
  };

  const remove = async (id: number) => {
    const res = (await api.delete(`/mercadolibre/messages/templates/${id}?seller_id=${sellerId}`)) as any;
    setConfirmDelete(null);
    if (!res.success) {
      setError(res.error || 'No se pudo eliminar');
      return;
    }
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setEditing(null);
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/30 hover:bg-blue-500/20"
        title="Elegir un texto predeterminado para pegarlo en el cuadro"
      >
        <FileText className="w-3.5 h-3.5" /> Textos predeterminados
      </button>

      {open && (
        <div className={`absolute bottom-full left-0 mb-2 w-[min(28rem,calc(100vw-2rem))] ${compact ? 'max-h-[300px]' : 'max-h-[420px]'} flex flex-col bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl shadow-xl z-10`}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)]">
            <span className="text-xs font-semibold text-[var(--color-text)]">
              {editing === null
                ? 'Textos predeterminados'
                : editing === 'new'
                  ? 'Nuevo texto predeterminado'
                  : 'Editar texto predeterminado'}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-[var(--color-surface-hover)]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {error && (
            <div className="px-3 py-1.5 text-xs text-[var(--color-error)] border-b border-[var(--color-border)]">
              {error}
            </div>
          )}

          {editing !== null ? (
            <TemplateEditor
              initial={editing === 'new' ? null : templates.find((t) => t.id === editing) || null}
              tags={tags}
              vars={vars || {}}
              sellerId={sellerId}
              canManageShared={canManageShared}
              onCancel={() => setEditing(null)}
              onSaved={(saved) => {
                setTemplates((prev) => {
                  const rest = prev.filter((t) => t.id !== saved.id);
                  return [...rest, saved].sort((a, b) =>
                    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
                  );
                });
                setEditing(null);
              }}
            />
          ) : loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-muted)]" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)]">
                <div className="flex-1 flex items-center gap-1.5 px-2 py-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg">
                  <Search className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar texto..."
                    className="flex-1 bg-transparent text-xs text-[var(--color-text)] focus:outline-none"
                    autoFocus
                  />
                </div>
                {canEdit && <button
                  type="button"
                  onClick={() => setEditing('new')}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-[var(--color-primary)] text-white hover:opacity-90"
                >
                  <Plus className="w-3.5 h-3.5" /> Nuevo
                </button>}
              </div>
              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-[var(--color-text-muted)]">
                    {templates.length === 0
                      ? canEdit
                        ? 'Todavía no hay textos. Creá el primero con "Nuevo".'
                        : 'Todavía no hay textos predeterminados.'
                      : 'Ningún texto coincide con la búsqueda.'}
                  </div>
                ) : (
                  filtered.map((t) => (
                    <div
                      key={t.id}
                      className="group px-3 py-2 border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-surface-hover)]"
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => applyTemplate(t)}
                          className="flex-1 min-w-0 text-left"
                          title="Pegar en el cuadro de respuesta"
                        >
                          <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text)]">
                            <span className="truncate">{t.name}</span>
                            {t.seller_id === null && (
                              <span className="shrink-0 px-1.5 py-px rounded-full text-[10px] font-normal bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)]">
                                Todos los vendedores
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[var(--color-text-muted)] line-clamp-2 whitespace-pre-line">
                            {t.body}
                          </div>
                        </button>
                        {confirmDelete === t.id ? (
                          <div className="flex items-center gap-1 shrink-0 text-[11px]">
                            <span className="text-[var(--color-text-muted)]">¿Eliminar?</span>
                            <button
                              type="button"
                              onClick={() => remove(t.id)}
                              className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 hover:bg-red-500/25"
                            >
                              Sí
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(null)}
                              className="px-1.5 py-0.5 rounded hover:bg-[var(--color-surface-hover)]"
                            >
                              No
                            </button>
                          </div>
                        ) : !canEdit || (t.seller_id === null && !canManageShared) ? null : (
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => setEditing(t.id)}
                              className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)]"
                              title="Editar"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(t.id)}
                              className="p-1 rounded text-[var(--color-text-muted)] hover:text-red-500 hover:bg-[var(--color-surface)]"
                              title="Eliminar"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Editor: nombre + texto + lista de etiquetas para insertar ────────────── */

function TemplateEditor({
  initial,
  tags,
  vars,
  sellerId,
  canManageShared,
  onCancel,
  onSaved,
}: {
  initial: Template | null;
  tags: TagDef[];
  vars: Record<string, string>;
  sellerId: number;
  canManageShared: boolean;
  onCancel: () => void;
  onSaved: (t: Template) => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [body, setBody] = useState(initial?.body || '');
  // Nuevo: compartido por defecto para quien puede; editar: conserva lo que era.
  const [shared, setShared] = useState(initial ? initial.seller_id === null : canManageShared);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  /** Mete la etiqueta donde está el cursor (o al final) y deja el cursor después. */
  const insertTag = (key: string) => {
    const tag = `{{${key}}}`;
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = body.slice(0, start) + tag + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const preview = useMemo(() => fillTemplate(body, vars, tags).text, [body, vars, tags]);

  const save = async () => {
    if (!name.trim() || !body.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), body: body.trim(), shared };
      const res = (initial
        ? await api.put(`/mercadolibre/messages/templates/${initial.id}?seller_id=${sellerId}`, payload)
        : await api.post(`/mercadolibre/messages/templates?seller_id=${sellerId}`, payload)) as any;
      if (!res.success) throw new Error(res.error || 'No se pudo guardar');
      onSaved(res.data);
    } catch (e: any) {
      setError(e.message || 'Error de red');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-2">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nombre (ej: Aviso de envío)"
        maxLength={80}
        className="w-full px-2.5 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-xs text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
        autoFocus
      />
      <textarea
        ref={bodyRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Hola {{nombre}}, tu compra ya fue enviada. Número de seguimiento: {{tracking_ml}}"
        rows={5}
        className="w-full px-2.5 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-xs text-[var(--color-text)] resize-y focus:outline-none focus:border-[var(--color-primary)]"
      />
      <div>
        <div className="flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] mb-1">
          <Tag className="w-3 h-3" /> Tocá una etiqueta para meterla donde está el cursor:
        </div>
        <div className="flex flex-wrap gap-1">
          {tags.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => insertTag(t.key)}
              title={`Se reemplaza por: ${t.label}`}
              className="px-2 py-0.5 rounded-full text-[11px] bg-blue-500/10 text-blue-600 border border-blue-500/30 hover:bg-blue-500/20"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {body.trim() && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-0.5">
            Así queda con los datos de esta venta
          </div>
          <div className="text-xs text-[var(--color-text)] whitespace-pre-line">{preview}</div>
          <div
            className={`text-[10px] mt-1 ${preview.length > ML_MAX_CHARS ? 'text-red-500' : 'text-[var(--color-text-muted)]'}`}
          >
            {preview.length} / {ML_MAX_CHARS} letras
            {preview.length > ML_MAX_CHARS && ' — Mercado Libre no acepta mensajes tan largos'}
          </div>
        </div>
      )}
      {canManageShared && (
        <label className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)] cursor-pointer">
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          Usarlo en todos los vendedores (si no, solo en el vendedor de esta venta)
        </label>
      )}
      {error && <div className="text-xs text-[var(--color-error)]">{error}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-xs hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !name.trim() || !body.trim()}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Guardar
        </button>
      </div>
    </div>
  );
}
